import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Check, Loader2, FileText } from "lucide-react";
import Layout from "@/components/Layout";
import { supabase } from "@/integrations/supabase/client";

type OrderRow = {
  id: string;
  status: string;
  payment_status: string | null;
  total_cents: number | null;
  currency: string | null;
  receipt_url: string | null;
  order_mode: string | null;
};

const OrderReturnPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [order, setOrder] = useState<OrderRow | null>(null);
  const [status, setStatus] = useState<"loading" | "ok" | "pending" | "missing">("loading");

  useEffect(() => {
    if (!sessionId) {
      setStatus("missing");
      return;
    }

    let cancelled = false;
    const poll = async (attempt = 0) => {
      const { data } = await (supabase as any)
        .from("orders")
        .select("id, status, payment_status, total_cents, currency, receipt_url, order_mode")
        .eq("stripe_session_id", sessionId)
        .maybeSingle();

      if (cancelled) return;

      if (data?.payment_status === "paid") {
        setOrder(data as OrderRow);
        setStatus("ok");
        return;
      }

      if (attempt < 8) {
        setOrder(data as OrderRow | null);
        setStatus("pending");
        setTimeout(() => poll(attempt + 1), 1500);
      } else {
        setOrder(data as OrderRow | null);
        setStatus(data ? "pending" : "missing");
      }
    };
    poll();

    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <Layout>
      <section className="min-h-screen bg-gradient-to-b from-coral-400/20 via-cream-100 to-cream-50 flex items-center justify-center px-6 py-24">
        <div className="max-w-lg w-full bg-white/60 backdrop-blur-sm rounded-2xl p-10 md:p-14 text-center">
          {status === "loading" || status === "pending" ? (
            <>
              <Loader2 className="w-10 h-10 text-foreground animate-spin mx-auto mb-6" />
              <span className="font-serif italic text-3xl text-foreground">Bearbetar</span>
              <h1 className="text-xl font-bold uppercase tracking-wider text-foreground mt-1">
                Din betalning
              </h1>
              <p className="text-sm text-muted-foreground mt-4">
                Vi bekräftar din betalning. Det kan ta några sekunder.
              </p>
            </>
          ) : status === "ok" && order ? (
            <>
              <div className="w-14 h-14 rounded-full bg-foreground text-background flex items-center justify-center mx-auto mb-6">
                <Check className="w-7 h-7" />
              </div>
              <span className="font-serif italic text-3xl text-foreground">Tack</span>
              <h1 className="text-xl font-bold uppercase tracking-wider text-foreground mt-1">
                För din beställning
              </h1>
              <p className="text-sm text-muted-foreground mt-4">
                {order.order_mode === "subscription"
                  ? "Din prenumeration är aktiverad. Vi skickar dispenser och första leverans inom kort."
                  : "Din beställning är bekräftad. Vi skickar leveransinformation till din e-post."}
              </p>
              {order.total_cents != null && (
                <p className="text-2xl font-bold text-foreground mt-6 tabular-nums">
                  {(order.total_cents / 100).toFixed(0)} {order.currency || "SEK"}
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                {order.receipt_url && (
                  <a
                    href={order.receipt_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 border border-foreground text-foreground bg-transparent py-3.5 rounded-lg font-medium uppercase tracking-widest text-[10px] hover:bg-foreground hover:text-background transition-all duration-300 inline-flex items-center justify-center gap-2"
                  >
                    <FileText className="w-3.5 h-3.5" /> Kvitto
                  </a>
                )}
                <Link
                  to="/account"
                  className="flex-1 bg-foreground text-background py-3.5 rounded-lg font-medium uppercase tracking-widest text-[10px] hover:bg-foreground/90 transition-all duration-300 inline-flex items-center justify-center gap-2"
                >
                  Mitt konto <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </>
          ) : (
            <>
              <span className="font-serif italic text-3xl text-foreground">Inget</span>
              <h1 className="text-xl font-bold uppercase tracking-wider text-foreground mt-1">
                Att visa
              </h1>
              <p className="text-sm text-muted-foreground mt-4">
                Vi kunde inte hitta din beställning. Kontakta oss på info@aquadias.se om du tror något gått fel.
              </p>
              <Link
                to="/account"
                className="mt-8 inline-flex items-center gap-2 bg-foreground text-background py-3.5 px-6 rounded-lg font-medium uppercase tracking-widest text-[10px] hover:bg-foreground/90 transition-all duration-300"
              >
                Till mitt konto <ArrowRight className="w-3 h-3" />
              </Link>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default OrderReturnPage;
